import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

import HomeSectionTitle from "./components/home-section-title";
import HomeSectionDescription from "./components/home-section-description";
import { cn } from "@/lib/utils";
import HomeStack from "./home-stack";
import { SquareMousePointer, Code2, TrendingUp } from "lucide-react";
import ButtonOutline from "@/components/ui/button-outline";
import FadeInAnimation from "@/components/animations/FadeInAnimation";

const aboutData = [
  {
    title: "2019",
    description:
      "2019년 웹 퍼블리싱으로 입문한 뒤 React와 Next.js로 전향하며 프론트엔드 역량을 꾸준히 확장해왔습니다.",
  },
  {
    title: "2000",
    description:
      "2년간 2,000회 이상 커밋하며 학습과 실험을 꾸준히 이어가고 있습니다. Next.js 15, React 19 등 최신 스택을 지속적으로 탐구합니다.",
  },
];

const about_cont = [
  {
    title: "프론트엔드 개발",
    description:
      "기획부터 디자인·개발·배포까지 경험하며, React와 Next.js의 구조 설계와 렌더링 원리를 깊이 이해합니다.",
    emoji: Code2,
  },
  {
    title: "사용자 중심의 UI·UX",
    description:
      "최신 디자인 시스템을 활용해 일관된 퍼블리싱과 접근성 높은 인터랙티브 UI를 고려합니다.",
    emoji: SquareMousePointer,
  },
  {
    title: "지속적인 성장",
    description:
      "새로운 기술을 학습하고 블로그를 통해 지식과 경험을 정리·공유합니다.",
    emoji: TrendingUp,
  },
];

const HomeAbout = () => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const numRefs = useRef<HTMLSpanElement[]>([]);

  useGSAP(() => {
    numRefs.current.forEach((el, i) => {
      if (!el) return;

      const targetValue = i === 0 ? 2019 : 2000;
      const obj = { val: 0 };

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 90%",
          toggleActions: "play none none reverse",
        },
      });
      // 페이드인
      tl.from(el, {
        opacity: 0,
        y: 30,
        duration: 0.5,
        ease: "power2.out",
      });

      // 숫자 증가
      tl.to(
        obj,
        {
          val: targetValue,
          duration: 1.5,
          ease: "power2.out",
          onUpdate: () => {
            const value = Math.floor(obj.val);
            el.textContent = i === 0 ? String(value) : value.toLocaleString();
          },
        },
        "-=0.7"
      );
    });
  }, []);

  return (
    <div ref={scrollerRef} className="flex flex-col z-11 relative">
      <div className="text-zinc-900 relative grid lg:gap-16">
        {/* 상단 인트로 */}
        <FadeInAnimation>
          <div className="flex flex-col gap-5 items-start">
            <HomeSectionTitle className="relative inline-block text-3xl leading-relaxed">
              안녕하세요! <br /> <span>프론트엔드 개발자</span> Phm' 입니다.
              <br />
            </HomeSectionTitle>
          </div>
        </FadeInAnimation>

        {/* 메인 레이아웃 */}
        <div className="grid lg:grid-cols-[1fr_3fr] gap-20 md:gap-40">
          <div className="relative">
            <div className="lg:sticky top-40">
              {" "}
              <FadeInAnimation>
                <HomeSectionDescription className="max-w-[800px] text-base mb-10 leading-relaxed">
                  에이전시 경험을 기반으로 사용자 중심의 직관적인 인터페이스
                  설계와 UI/UX와 기술을 아우르는 하이브리드 전문가로 성장하고
                  있습니다.
                </HomeSectionDescription>
                <ButtonOutline name="About me" path="/about" />{" "}
              </FadeInAnimation>
            </div>
          </div>{" "}
          <div className="flex flex-col gap-40">
            {/* 숫자 카드 */}{" "}
            <FadeInAnimation>
              <div className="grid md:grid-cols-2 gap-8 md:gap-10 w-full">
                {aboutData.map((e, idx) => (
                  <div
                    key={`${e.title}:${idx}`}
                    className={cn("relative rounded-2xl group flex flex-col")}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-[10px] text-zinc-500 tracking-wider">
                        value 0{idx + 1}
                      </span>
                    </div>
                    <div className="items-center gap-20">
                      <div className="flex flex-col">
                        <h1 className="text-5xl grid grid-cols-[auto_3fr] font-semibold gap-3 items-center text-zinc-100">
                          <span
                            className="inline-block"
                            ref={(el) => (numRefs.current[idx] = el!)}
                          >
                            {e.title}
                          </span>
                          {idx === 0 ? "~" : "+"}
                        </h1>
                      </div>
                      <p className="text-base pt-5 text-zinc-400 leading-relaxed break-keep max-w-[500px]">
                        {e.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </FadeInAnimation>
            {/* 기타 카드 */}
            <FadeInAnimation>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-8 w-full">
                {about_cont.map((e, idx) => (
                  <div
                    key={`${e.title}:${idx}`}
                    className="relative rounded-2xl group"
                  >
                    <div className="relative h-full z-10">
                      <div className="flex items-start justify-between mb-2 pb-2">
                        <span className="text-[10px] text-zinc-500 tracking-wider">
                          value 0{idx + 1}
                        </span>
                      </div>
                      <div className="flex flex-col gap-4">
                        <h1 className="font-semibold flex gap-3 items-center text-zinc-100 pb-1">
                          <span className="text-xl">{e.title}</span>
                        </h1>
                        <p className="text-sm text-zinc-400 leading-relaxed break-keep">
                          {e.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </FadeInAnimation>
            <FadeInAnimation>
              <HomeStack />
            </FadeInAnimation>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeAbout;
